import { useCallback, useEffect, useMemo, useState } from "react";

import { getMenuCategories, getMenuItems } from "../api/api";
import { Button } from "../components/Button";

function MenuItemRow({ item, expandedId, onToggle }) {
  const rowKey = String(item.id);
  const hasDesc = Boolean(item.description?.trim());
  const isOpen = hasDesc && expandedId === rowKey;
  const titleId = `menu-item-${rowKey}-title`;
  const panelId = `menu-item-${rowKey}-desc`;

  if (!hasDesc) {
    return (
      <li className="menuList__item">
        <div className="menuList__trigger menuList__trigger--static">
          <span className="menuList__title">{item.title}</span>
          <strong className="menuList__price">{item.price_rub} руб.</strong>
        </div>
      </li>
    );
  }

  return (
    <li
      className={`menuList__item menuList__item--expandable${isOpen ? " menuList__item--open" : ""}`}
    >
      <button
        type="button"
        className="menuList__trigger"
        id={titleId}
        aria-expanded={isOpen}
        aria-controls={panelId}
        onClick={() => onToggle(rowKey)}
      >
        <span className="menuList__title">{item.title}</span>
        <span className="menuList__meta">
          <span className="menuList__more">
            {isOpen ? "свернуть" : "подробнее"}
          </span>
          <strong className="menuList__price">{item.price_rub} руб.</strong>
        </span>
      </button>
      <div
        className="menuList__panel"
        id={panelId}
        role="region"
        aria-labelledby={titleId}
        aria-hidden={!isOpen}
      >
        <div className="menuList__panelInner">
          <p className="menuList__desc">{item.description}</p>
        </div>
      </div>
    </li>
  );
}

export function Menu({ onBack }) {
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [expandedId, setExpandedId] = useState(null);

  const toggleItem = useCallback((id) => {
    setExpandedId((prev) => (prev === id ? null : id));
  }, []);

  useEffect(() => {
    let mounted = true;
    Promise.all([getMenuCategories(), getMenuItems()])
      .then(([cats, menuItems]) => {
        if (!mounted) return;
        setCategories(Array.isArray(cats) ? cats : []);
        setItems(Array.isArray(menuItems) ? menuItems : []);
      })
      .catch((err) => {
        if (!mounted) return;
        setError(err?.message || "Не удалось загрузить меню");
      })
      .finally(() => {
        if (!mounted) return;
        setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, []);

  const tree = useMemo(() => {
    const byParent = new Map();
    for (const cat of categories) {
      const key = cat.parent_id ?? 0;
      const arr = byParent.get(key) || [];
      arr.push(cat);
      byParent.set(key, arr);
    }
    for (const arr of byParent.values()) {
      arr.sort((a, b) => (a.sort_order - b.sort_order) || (a.id - b.id));
    }
    const byCategory = new Map();
    for (const item of items) {
      const arr = byCategory.get(item.category_id) || [];
      arr.push(item);
      byCategory.set(item.category_id, arr);
    }
    for (const arr of byCategory.values()) {
      arr.sort(
        (a, b) =>
          (Number(a.sort_order ?? 0) - Number(b.sort_order ?? 0)) || a.id - b.id,
      );
    }
    return { byParent, byCategory };
  }, [categories, items]);

  const topLevel = tree.byParent.get(0) || [];

  const renderItemList = (list) => (
    <ul className="menuList">
      {list.map((item) => (
        <MenuItemRow
          key={item.id}
          item={item}
          expandedId={expandedId}
          onToggle={toggleItem}
        />
      ))}
    </ul>
  );

  return (
    <section className="client">
      <header className="sectionHeader">
        <h2>Меню</h2>
      </header>
      <div className="eventsToolbar">
        <Button onClick={onBack}>Назад</Button>
      </div>

      {loading ? <p className="muted">Загрузка...</p> : null}
      {!loading && error ? <p className="errorMessage">{error}</p> : null}

      {!loading && !error ? (
        <div className="menuSections">
          {topLevel.map((cat) => {
            const sub = tree.byParent.get(cat.id) || [];
            const ownItems = tree.byCategory.get(cat.id) || [];
            return (
              <section key={cat.id} className="menuSection">
                <h3>{cat.title}</h3>
                {ownItems.length > 0 ? renderItemList(ownItems) : null}
                {sub.map((subCat) => {
                  const subItems = tree.byCategory.get(subCat.id) || [];
                  return (
                    <div key={subCat.id} className="menuSubsection">
                      <h4>{subCat.title}</h4>
                      {renderItemList(subItems)}
                    </div>
                  );
                })}
              </section>
            );
          })}
        </div>
      ) : null}
    </section>
  );
}
